/** RAMS utility functions — risk scoring, banding, and display label maps.
 *  Shared by the on-screen document (features/hs/document/), the editor, the
 *  list, and the PDF export so all four agree on the same numbers and words. */

import type {
  ControlType,
  Hazard,
  HazardLikelihood,
  HazardSeverity,
  MethodStep,
  RamsArrangements,
  RamsHoldPoint,
  RamsListItem,
  RamsRecord,
  RamsStatus,
} from '../../types/healthSafety.ts';
import { liftingPlanBlockers, resolveDocumentType, resolveLiftingPlan } from './liftingPlanUtils.ts';

/** The numbered sections of a RAMS, in reading order. The on-screen document,
 *  the PDF, the Word export and the editor all read numbers and headings from
 *  here, so a section added or moved in one place cannot drift in another. */
export const RAMS_SECTIONS = [
  { number: 1,  key: 'scope',         heading: 'Scope of works' },
  { number: 2,  key: 'standards',     heading: 'Legal and standards compliance' },
  { number: 3,  key: 'drawings',      heading: 'Drawings and documents' },
  { number: 4,  key: 'personnel',     heading: 'Key personnel and responsibilities' },
  { number: 5,  key: 'personsAtRisk', heading: 'Persons at risk' },
  { number: 6,  key: 'plant',         heading: 'Plant, equipment and materials' },
  { number: 7,  key: 'coshh',         heading: 'Substances and materials (COSHH)' },
  { number: 8,  key: 'permits',       heading: 'Permits and pre-start checks' },
  { number: 9,  key: 'method',        heading: 'Method statement' },
  { number: 10, key: 'holdPoints',    heading: 'Inspection and test plan hold points' },
  { number: 11, key: 'risk',          heading: 'Risk assessment' },
  { number: 12, key: 'environmental', heading: 'Environmental controls' },
  { number: 13, key: 'ppe',           heading: 'Personal protective equipment' },
  { number: 14, key: 'emergency',     heading: 'Emergency and first aid' },
  { number: 15, key: 'quality',       heading: 'Quality records' },
  { number: 16, key: 'handover',      heading: 'Handover and completion' },
  { number: 17, key: 'references',    heading: 'References' },
  { number: 18, key: 'distribution',  heading: 'Distribution' },
  { number: 19, key: 'briefing',      heading: 'Briefing and sign-off' },
] as const;

export type RamsSectionKey = (typeof RAMS_SECTIONS)[number]['key'];

export function ramsSection(key: RamsSectionKey): { number: number; heading: string } {
  const section = RAMS_SECTIONS.find((candidate) => candidate.key === key)!;
  return { number: section.number, heading: section.heading };
}

export const RAMS_SIGN_OFF_ROLES = [
  'Supervisor',
  'Temporary works supervisor / coordinator',
  'Site manager',
] as const;
export const RAMS_SIGN_OFF_FIELDS = ['Signature', 'Print name', 'Date'] as const;
export const RAMS_OPERATIVE_COLUMNS = ['Name', 'Role', 'Company', 'Signature', 'Date'] as const;

export const RAMS_DECLARATION =
  'I confirm that I have read and understood this Risk Assessment and Method Statement, that I am '
  + 'competent to carry out the work described, and that I will work in accordance with the control '
  + 'measures set out above. I will stop work and inform the site supervisor if conditions change.';

/** A list row counts as content when either half has text; blank rows added
 *  in the editor and never filled in must print as "N/A", not as an empty bullet. */
export function ramsListItemsWithContent(items: readonly RamsListItem[] | undefined): RamsListItem[] {
  return (items ?? [])
    .filter((item) => item.label?.trim() || item.detail?.trim())
    // Rows written by another client may arrive without an id; renderers key on it.
    .map((item, index) => (item.id ? item : { ...item, id: `row-${index}` }));
}

export function ramsHoldPointsWithContent(items: readonly RamsHoldPoint[] | undefined): RamsHoldPoint[] {
  return (items ?? [])
    .filter((item) => item.activity?.trim() || item.checks?.trim())
    .map((item, index) => (item.id ? item : { ...item, id: `row-${index}` }));
}

/** Hold point refs are derived from position so reordering never leaves a
 *  stale or duplicate HP number. */
export function holdPointRef(index: number): string {
  return `HP${index + 1}`;
}

/** A, B, ... Z, AA, AB ... for phase headings. */
export function phaseLetter(index: number): string {
  let n = index;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (n % 26)) + letters;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letters;
}

export interface RamsMethodGroup {
  /** null for a run of steps with no phase, which render ungrouped. */
  letter: string | null;
  phase: string | null;
  /** `number` is the step's position in the whole sequence, so numbering
   *  runs on across phases rather than restarting. */
  steps: { step: MethodStep; number: number }[];
}

/** Consecutive steps sharing a phase (ignoring case and surrounding space)
 *  form one group. Letters are assigned to phased groups only, in order. */
export function groupMethodSteps(steps: readonly MethodStep[]): RamsMethodGroup[] {
  const groups: RamsMethodGroup[] = [];
  let lettered = 0;
  steps.forEach((step, index) => {
    const phase = step.phase?.trim() || null;
    const previous = groups[groups.length - 1];
    const sameRun = previous
      && (previous.phase?.toLocaleLowerCase() ?? null) === (phase?.toLocaleLowerCase() ?? null);
    if (sameRun) {
      previous.steps.push({ step, number: index + 1 });
      return;
    }
    groups.push({
      letter: phase ? phaseLetter(lettered++) : null,
      phase,
      steps: [{ step, number: index + 1 }],
    });
  });
  return groups;
}

/** Drops list rows and hold points left blank, and a blank briefing note, so
 *  saving never stores empty rows. Keys the record never had stay absent. */
export function compactArrangements(arrangements: RamsArrangements): RamsArrangements {
  const next: RamsArrangements = { ...arrangements };
  const listFields = [
    'standards', 'drawings', 'keyPersonnel', 'preStartChecks', 'environmentalControls',
    'qualityRecords', 'handover', 'references', 'distribution',
  ] as const;
  for (const field of listFields) {
    if (next[field] !== undefined) next[field] = ramsListItemsWithContent(next[field]);
  }
  if (next.holdPoints !== undefined) next.holdPoints = ramsHoldPointsWithContent(next.holdPoints);
  if (next.briefingNote !== undefined) {
    const note = next.briefingNote.trim();
    if (note) next.briefingNote = note; else delete next.briefingNote;
  }
  return next;
}

/** Trims `phase` and removes it when blank so an untouched step stays byte-identical. */
export function compactMethodSteps(steps: readonly MethodStep[]): MethodStep[] {
  return steps.map((step) => {
    if (step.phase === undefined) return step;
    const { phase, ...rest } = step;
    const trimmed = phase.trim();
    return trimmed ? { ...rest, phase: trimmed } : rest;
  });
}

export const SEVERITY_WEIGHT: Record<HazardSeverity, number> = {
  negligible:   1,
  minor:        2,
  moderate:     3,
  major:        4,
  catastrophic: 5,
};

export const LIKELIHOOD_WEIGHT: Record<HazardLikelihood, number> = {
  almost_certain: 5,
  likely:         4,
  possible:       3,
  unlikely:       2,
  rare:           1,
};

export function riskScore(severity: HazardSeverity, likelihood: HazardLikelihood): number {
  return (SEVERITY_WEIGHT[severity] ?? 1) * (LIKELIHOOD_WEIGHT[likelihood] ?? 1);
}

export const SEVERITY_LABEL: Record<HazardSeverity, string> = {
  negligible:   'Negligible',
  minor:        'Minor',
  moderate:     'Moderate',
  major:        'Major',
  catastrophic: 'Catastrophic',
};

export const LIKELIHOOD_LABEL: Record<HazardLikelihood, string> = {
  almost_certain: 'Almost certain',
  likely:         'Likely',
  possible:       'Possible',
  unlikely:       'Unlikely',
  rare:           'Rare',
};

export const CONTROL_TYPE_ORDER: ControlType[] = [
  'elimination',
  'substitution',
  'engineering',
  'administrative',
  'ppe',
];

export const CONTROL_TYPE_LABEL: Record<ControlType, string> = {
  elimination:    'Elimination',
  substitution:   'Substitution',
  engineering:    'Engineering',
  administrative: 'Administrative',
  ppe:            'PPE',
};

const LEGACY_SEVERITY_MAP: Record<string, HazardSeverity> = {
  low:      'minor',
  medium:   'moderate',
  high:     'major',
  critical: 'catastrophic',
};

/** Back-compat is a READ-TIME NORMALISER, not a data migration: a hazard
 *  saved before the 5-point scale shipped still reads as `low`/`medium`/
 *  `high`/`critical` in storage. Side effect (accepted): a legacy `low`
 *  hazard re-computes from weight 1 to 2 the next time it's edited and
 *  saved — nothing is backfilled. */
export function normaliseSeverity(value: string | null | undefined): HazardSeverity {
  if (!value) return 'moderate';
  if (value in SEVERITY_WEIGHT) return value as HazardSeverity;
  return LEGACY_SEVERITY_MAP[value] ?? 'moderate';
}

export type RiskBandLabel = 'Low' | 'Medium' | 'High' | 'Unacceptable';

/** Risk band label based on score thresholds. `className` is border/bg/fg
 *  tokens only — call sites supply the pill shape (inline-flex, rounded-full,
 *  border, padding), since a bare span has nothing for border/bg to paint. */
export function riskBand(score: number): { label: RiskBandLabel; className: string } {
  if (score >= 17) return { label: 'Unacceptable', className: 'border-status-danger bg-status-danger text-white' };
  if (score >= 10) return { label: 'High',         className: 'border-status-danger-border bg-status-danger-bg text-status-danger-foreground' };
  if (score >= 5)  return { label: 'Medium',       className: 'border-status-warning-border bg-status-warning-bg text-status-warning-foreground' };
  return                  { label: 'Low',          className: 'border-status-success-border bg-status-success-bg text-status-success-foreground' };
}

/** HSE's five steps for managing risk end in "record findings" and "review
 *  the controls" — these lines are what the document and PDF print for each
 *  band under "How risk is rated". The Medium line keeps HSE's own phrase,
 *  "so far as is reasonably practicable". */
export const RISK_BAND_GUIDANCE: Record<RiskBandLabel, string> = {
  Low:          'Broadly acceptable. Monitor and reduce further where practicable.',
  Medium:       'Tolerable. Reduce so far as is reasonably practicable.',
  High:         'Reduce before work starts. The supervisor must confirm controls are in place.',
  Unacceptable: 'Do not proceed. The work must be re-planned.',
};

export const RISK_REVIEW_TRIGGER_LINE =
  'Review this assessment if the work, the site or the people change, after any accident or near miss, and no later than the review date.';

/** Risk after controls. Returns null when unassessed and never falls back to
 *  the initial score — a hazard with no residual assessment yet must read as
 *  "not assessed", not as "no better than it started". */
export function residualScore(hazard: Hazard): number | null {
  if (typeof hazard.residualRiskScore === 'number') return hazard.residualRiskScore;
  if (hazard.residualSeverity && hazard.residualLikelihood) {
    return riskScore(normaliseSeverity(hazard.residualSeverity), hazard.residualLikelihood);
  }
  return null;
}

/** Single sortable risk signal for the list view: residual once assessed,
 *  otherwise initial. The list needs one number per row; the document (see
 *  RamsRiskTable) must never conflate the two, so this helper stays out of it. */
export function highestRisk(hazards: Hazard[]): { score: number; label: RiskBandLabel; className: string } | null {
  if (hazards.length === 0) return null;
  let top = -Infinity;
  for (const hazard of hazards) {
    const score = residualScore(hazard) ?? hazard.riskScore ?? riskScore(normaliseSeverity(hazard.severity), hazard.likelihood);
    if (score > top) top = score;
  }
  return { score: top, ...riskBand(top) };
}

/** Reasons a draft can't be sent for review yet — drives both the on-screen
 *  "Not ready to send for review" notice and the header's canIssue flag
 *  (kept named after the old draft->issued gate; it now gates draft->submitted,
 *  the completeness bar the record must clear before it can go out at all).
 *  The server route gates the actual transition independently; this is UX,
 *  not the gate. */
export function issueBlockers(
  record: Pick<RamsRecord, 'methodStatement' | 'hazards' | 'documentType' | 'liftingPlan' | 'arrangements'>
    & Partial<Pick<RamsRecord, 'substances' | 'coshhDocuments'>>,
): string[] {
  const reasons: string[] = [];
  if (!record.methodStatement || record.methodStatement.length === 0) {
    reasons.push('the method statement');
  }
  if (record.hazards.length === 0) {
    reasons.push('at least one hazard');
  } else {
    const missingControls = record.hazards.filter(h => h.controls.length === 0).length;
    if (missingControls > 0) {
      reasons.push(`control measures for ${missingControls} hazard${missingControls === 1 ? '' : 's'}`);
    }
  }
  if (resolveDocumentType(record) === 'lifting_plan') {
    reasons.push(...liftingPlanBlockers(resolveLiftingPlan(record.liftingPlan)));
  }
  if (record.arrangements) {
    if (isUnresolvedRamsValue(record.arrangements.supervisorName)) reasons.push('the site supervisor');
    if (isUnresolvedRamsValue(record.arrangements.emergency?.firstAiders)) reasons.push('the nominated first aider(s)');
    if (isUnresolvedRamsValue(record.arrangements.emergency?.musterPoint)) reasons.push('the site muster point');
  }
  const substances = record.substances ?? [];
  const missingAssessments = substances.filter((substance) =>
    !(record.coshhDocuments ?? []).some((assignment) =>
      assignment.substanceId === substance.id
      && assignment.document.documentType === 'coshh_assessment'
      && assignment.document.status === 'active'
    )
  ).length;
  if (missingAssessments > 0) {
    reasons.push(`controlled COSHH assessment${missingAssessments === 1 ? '' : 's'} for ${missingAssessments} substance${missingAssessments === 1 ? '' : 's'}`);
  }
  return reasons;
}

/** Consistent document-safe copy for any field or section without a value. */
export function ramsEmptyCopy(_status: RamsStatus): string {
  return 'N/A';
}

/** `docRef` is the human-facing reference on the masthead, cover block, PDF
 *  filename and PDF header — separate from `version` (the revision number),
 *  which the old PDF conflated by printing "RAMS-{version}" as "Reference". */
export function ramsDocRef(record: Pick<RamsRecord, 'id' | 'scope'>): string {
  return record.scope?.documentRef?.trim() || `RAMS-${record.id.slice(0, 4).toUpperCase()}`;
}

/** Narrows a list to the records matching every word typed, in any order,
 *  against the document reference, task, location, issuer and status.
 *  A blank query returns the same array so the caller's memo stays stable. */
export function searchRams<T extends Pick<RamsRecord, 'id' | 'scope' | 'taskDescription' | 'location' | 'issuedBy' | 'status'>>(
  records: T[],
  query: string,
): T[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return records;
  return records.filter((r) => {
    const text = [ramsDocRef(r), r.taskDescription, r.location, r.issuedBy, STATUS_LABEL[r.status]].join(' ').toLowerCase();
    return words.every((w) => text.includes(w));
  });
}

export const STATUS_LABEL: Record<RamsStatus, string> = {
  draft:      'Draft',
  submitted:  'Submitted',
  approved:   'Approved',
  issued:     'Issued',
  superseded: 'Superseded',
  archived:   'Archived',
};

/** Badge colour tokens, single source for RAMSList's table badge and
 *  RamsDocument's masthead badge (previously duplicated in both — see the
 *  review-workflow migration). `undefined` means "no colour token, render
 *  with the neutral secondary badge variant instead" — archived and
 *  superseded are both retired-document states, not active-status ones. */
export const STATUS_TOKEN_CLASS: Record<RamsStatus, string | undefined> = {
  draft:      'border-status-neutral-border bg-status-neutral-bg text-status-neutral-foreground hover:bg-status-neutral-bg',
  submitted:  'border-status-warning-border bg-status-warning-bg text-status-warning-foreground hover:bg-status-warning-bg',
  approved:   'border-status-success-border bg-status-success-bg text-status-success-foreground hover:bg-status-success-bg',
  issued:     'border-status-info-border bg-status-info-bg text-status-info-foreground hover:bg-status-info-bg',
  superseded: undefined,
  archived:   undefined,
};

const RAMS_STATUSES: readonly RamsStatus[] = ['draft', 'submitted', 'approved', 'issued', 'superseded', 'archived'];

/** Back-compat read-time normaliser for the review-before-issue lifecycle
 *  migration: legacy 'in_review' rows read back as 'submitted'. Old-meaning
 *  'approved' rows (post-issue sign-off) were rewritten to 'issued' by the
 *  migration itself — a string alone can't tell old- from new-meaning
 *  'approved' after that point, so this is belt-and-braces for whatever the
 *  one-off UPDATE missed, not the primary fix. Same shape as normaliseSeverity:
 *  called at read boundaries (RAMSService), never assume the DB value is
 *  already a valid RamsStatus. */
export function normaliseRamsStatus(value: string | null | undefined): RamsStatus {
  if (!value) return 'draft';
  if ((RAMS_STATUSES as readonly string[]).includes(value)) return value as RamsStatus;
  if (value === 'in_review') return 'submitted';
  return 'draft';
}

/** The review-before-issue state machine. Single source for the server
 *  transition route (imported across the Deno/frontend boundary, same
 *  pattern as email_service.tsx importing emails/templates.ts) and this
 *  module's own tests. 'superseded' has no allowed transitions out of it
 *  yet — Phase A wires it as a legal target (an issued revision superseded
 *  by a newer one) but nothing sets it: that needs a version-chain link
 *  between revisions that doesn't exist yet (Phase B territory). */
export const RAMS_ALLOWED_TRANSITIONS: Record<RamsStatus, RamsStatus[]> = {
  draft:      ['submitted'],
  submitted:  ['approved', 'draft'],
  approved:   ['issued', 'draft'],
  issued:     ['superseded', 'archived'],
  superseded: [],
  archived:   ['draft'],
};

export function canTransitionRams(from: string, to: string): boolean {
  const allowed = RAMS_ALLOWED_TRANSITIONS[normaliseRamsStatus(from)] ?? [];
  return (allowed as string[]).includes(to);
}

/** Live system data threaded down for cover-block auto-population: create-time
 *  seeding (RAMSEditor, RAMSTemplatePickerSheet), display-time fallback
 *  (resolveRamsCover below), and the PDF. `projectStartDate` is only ever
 *  shown as a hint, never written into a hazard-free-text field — the task's
 *  own start date is task-specific, not the project programme. */
export interface RamsProjectContext {
  projectName: string;
  /** Contractor preparing and carrying out the works, read from the project. */
  contractorName?: string | null;
  employerName: string;
  siteAddress: string;
  currentUserId: string;
  currentUserName: string;
  /** Profile-backed job title/position for document authorship. */
  currentUserRole?: string | null;
  projectStartDate?: string | null;
  /** Programme duration, already formatted for display (for example
   * "26 weeks" or "17 Aug 2026 – 16 Feb 2027"). */
  projectDuration?: string | null;
  /** Employer (MainContractor) link + its contact roster, threaded from
   *  ProjectDetail so RAMSSubmitForReviewDialog can pre-populate the
   *  reviewer picker from the project's own employer contacts rather than
   *  requiring a free-typed email every time. Optional: older call sites
   *  (tests, the PDF export) never set these and don't need to. */
  mainContractorId?: string | null;
  mainContractorContactIds?: string[];
}

/** Adds `months` calendar months to `from` (defaults to now) and returns an
 *  ISO date (yyyy-mm-dd), matching the value shape `<input type="date">`
 *  reads and writes throughout the editor. */
export function addMonthsIso(months: number, from: Date = new Date()): string {
  const d = new Date(from);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export interface RamsCoverResolution {
  contractorName: string | null;
  employerName: string | null;
  siteAddress: string | null;
  principalContractor: string | null;
  startDate: string | null;
  preparedBy: string | null;
  preparedByRole: string | null;
  duration: string | null;
}

/** Legacy generated RAMS used bracketed "To confirm" copy as if it were
 * factual data. Treat it as unresolved so authoritative project/user data
 * can populate the document. This deliberately does not treat ordinary
 * prose containing "confirm" as empty. */
export function isUnresolvedRamsValue(value: string | null | undefined): boolean {
  const text = value?.trim();
  if (!text) return true;
  return /^\[(?:to confirm|tbc)\b/i.test(text)
    || /^(?:to confirm|tbc|not recorded)$/i.test(text);
}

export function resolveRamsValue(
  stored: string | null | undefined,
  fallback: string | null | undefined,
): string | null {
  if (!isUnresolvedRamsValue(stored)) return stored!.trim();
  return !isUnresolvedRamsValue(fallback) ? fallback!.trim() : null;
}

/** Cover-block fallback resolver used by BOTH the screen document
 *  (RamsDocument) and the PDF (ramsPdfExport) so they can never disagree.
 *  The record's factual `scope` values win for document-specific fields;
 *  contractor is the exception because it is an authoritative live project
 *  party. Blank and legacy bracketed "To confirm" placeholders yield to
 *  authoritative project/user data.
 *  `preparedBy` and its role only
 *  falls back to the viewer's name when they are the record's creator;
 *  never attributes authorship to whoever happens to be reading it. Once a
 *  RAMS is issued, the server has already snapshotted employer/site address
 *  onto `scope` (see hs_rams_routes.tsx's transition handler), so this
 *  fallback path only matters pre-issue or for pre-existing records. */
export function resolveRamsCover(
  record: Pick<RamsRecord, 'scope' | 'createdBy'>,
  ctx: RamsProjectContext | null | undefined,
): RamsCoverResolution {
  const scope = record.scope;
  const authorContextAllowed = !!(ctx && (!record.createdBy || record.createdBy === ctx.currentUserId));
  return {
    // The contractor is a live project party, not a viewer/workspace
    // attribute. Prefer the project context so an existing RAMS created under
    // a different workspace cannot continue displaying a stale contractor.
    // Keep the stored value as a backwards-compatible fallback for contexts
    // that pre-date project-party threading (exports/tests/legacy callers).
    contractorName: resolveRamsValue(ctx?.contractorName, scope?.contractorName),
    employerName: resolveRamsValue(scope?.employerName, ctx?.employerName),
    siteAddress: resolveRamsValue(scope?.siteAddress, ctx?.siteAddress),
    principalContractor: resolveRamsValue(scope?.principalContractor, ctx?.employerName),
    startDate: resolveRamsValue(scope?.startDate, ctx?.projectStartDate?.slice(0, 10)),
    preparedBy: resolveRamsValue(scope?.preparedBy, authorContextAllowed ? ctx?.currentUserName : null),
    preparedByRole: resolveRamsValue(scope?.preparedByRole, authorContextAllowed ? ctx?.currentUserRole : null),
    duration: resolveRamsValue(scope?.duration, ctx?.projectDuration),
  };
}

/** The "Document control" block that opens the document, in print order. One
 *  builder for the screen, PDF and Word export so labels and order cannot
 *  differ; each caller passes its own date formatter. Values that are absent
 *  come back as 'N/A' (never empty) except Issued / Approved, which say so. */
export function ramsDocumentControl(
  record: RamsRecord,
  ctx: RamsProjectContext | null | undefined,
  formatDate: (iso: string | null | undefined) => string | null,
): { label: string; value: string }[] {
  const cover = resolveRamsCover(record, ctx);
  const orNA = (value: string | null | undefined) => (isUnresolvedRamsValue(value) ? 'N/A' : value!.trim());
  const withDate = (name: string, iso: string | null | undefined) =>
    [name.trim(), formatDate(iso)].filter(Boolean).join(', ');
  return [
    { label: 'RAMS title',                      value: orNA(record.taskDescription) },
    { label: 'Project',                         value: orNA(ctx?.projectName) },
    { label: 'Project no.',                     value: orNA(record.scope?.projectNumber) },
    { label: 'Reference',                       value: ramsDocRef(record) },
    { label: 'Revision',                        value: `Revision ${record.version}` },
    { label: 'Status',                          value: STATUS_LABEL[record.status] },
    { label: 'Contractor',                      value: orNA(cover.contractorName) },
    { label: 'Employer / principal contractor', value: orNA(cover.principalContractor || cover.employerName) },
    { label: 'Principal designer',              value: orNA(record.scope?.principalDesigner) },
    { label: 'Site address',                    value: orNA(cover.siteAddress) },
    { label: 'Work area',                       value: orNA(record.location) },
    { label: 'Start date',                      value: orNA(formatDate(cover.startDate)) },
    { label: 'Duration',                        value: orNA(cover.duration) },
    { label: 'Prepared by',                     value: orNA([cover.preparedBy, cover.preparedByRole].filter(Boolean).join(', ')) },
    { label: 'Issued',                          value: record.issuedBy ? withDate(record.issuedBy, record.issuedAt) : 'Not yet issued' },
    { label: 'Approved',                        value: record.approvedBy ? withDate(record.approvedBy, record.approvedAt) : 'Not yet approved' },
    { label: 'Review due',                      value: orNA(formatDate(record.reviewDueDate)) },
  ];
}
